import { CommonHelperFunctions } from "@kuika/kuika-cl-model";
import Axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { BOARD_CLIENT_ID } from "../components/management/board/board-client-id";
import { trackBoardWrite } from "../components/management/board/board-pending-writes";
import { ImportIssuePayload, ImportResult } from "../components/management/board/import-types";
import {
  ArtifactKind,
  ArtifactLink,
  Attachment,
  AutomationAction,
  AutomationRule,
  AutomationTrigger,
  Board,
  BoardColumn,
  BoardTransition,
  BoardConfig,
  BoardMode,
  BoardNotification,
  BoardReport,
  BoardScope,
  BoardStatus,
  BoardView,
  CreateIssueInput,
  BoardSyncResult,
  CustomFieldDef,
  EpicCard,
  CustomFieldType,
  Issue,
  IssueCard,
  IssueComment,
  IssueType,
  LinkPreview,
  IssueDetailBundle,
  IssueLink,
  IssueLinkType,
  IssuePage,
  IssueQueryParams,
  MoveIssueInput,
  Project,
  RoadmapRow,
  Sprint,
  SprintBurndown,
  UpdateIssueInput,
  Version,
  VersionProgress
} from "../components/management/board/types";
import { KMainFunctions } from "../shared/utility/main-functions";

// REST client for the provisional in-memory board backend (CoreApi BoardController,
// v1/api/board). CoreApi already serialises camelCase (MVC web defaults), so no
// casing translation is needed. Two STRUCTURAL mismatches are mapped here because
// the backend store is throwaway (the EF phase will reshape it) and the frontend
// types are the real contract:
//   1. Board.scope (nested union) <-> flat scopeKind/projectKey/projectKeys.
//   2. UpdateIssueInput optional-field presence -> backend *Set sentinel flags.
// Everything else is a thin pass-through.

const boardUrl: () => string = (): string => `${CommonHelperFunctions.GetBackendUrl()}/board`;

// ── Backend-shaped wire interfaces ───────────────────────────────────────

// BoardDefinition as the backend serialises it (flat scope). Mapped to Board on read.
interface RawBoard {
  id: string;
  name: string;
  scopeKind: string;
  projectKey: string | null;
  projectKeys: string[] | null;
  mode?: BoardMode;
  wipLimits?: Record<string, number | null>;
  columns?: BoardColumn[] | null;
}

interface CreateBoardPayload {
  name: string;
  scopeKind: string;
  projectKey?: string;
  projectKeys?: string[];
  mode: BoardMode;
}

// Mirrors CoreApi UpdateIssueInput: non-sentinel fields apply when present, the
// *Set companions distinguish "leave alone" from "set to null/empty".
interface UpdateIssuePayload {
  summary?: string;
  descriptionMarkdown?: string;
  type?: string;
  priority?: string;
  statusId?: string;
  labels?: string[];
  customFields?: Record<string, string>;
  assigneeUserId?: string | null;
  assigneeUserIdSet?: boolean;
  storyPoints?: number | null;
  storyPointsSet?: boolean;
  startDate?: string | null;
  startDateSet?: boolean;
  dueDate?: string | null;
  dueDateSet?: boolean;
  sprintId?: string | null;
  sprintIdSet?: boolean;
  parentIssueId?: string | null;
  parentIssueIdSet?: boolean;
  epicId?: string | null;
  epicIdSet?: boolean;
  epicName?: string | null;
  epicNameSet?: boolean;
  epicColor?: string | null;
  epicColorSet?: boolean;
  originalEstimateMin?: number | null;
  originalEstimateMinSet?: boolean;
  remainingEstimateMin?: number | null;
  remainingEstimateMinSet?: boolean;
  timeSpentMin?: number | null;
  timeSpentMinSet?: boolean;
  fixVersionId?: string | null;
  fixVersionIdSet?: boolean;
}

interface AddLinkPayload {
  fromIssueId: string;
  toIssueId: string;
  type: IssueLinkType;
}

interface RequestAccessResult {
  notified: number;
}

export interface BoardAccessResult {
  canInitialize: boolean;
  accessRequested: boolean;
}

export interface IssuesExportResult {
  filename: string;
  csv: string;
}

// /config returns boards in the flat backend shape; map them like the snapshot does.
interface RawBoardConfig extends Omit<BoardConfig, "boards"> {
  boards: RawBoard[];
}

interface AttachmentContentResult {
  dataUrl: string | null;
}

// ── Translation helpers ──────────────────────────────────────────────────

function mapBoard(raw: RawBoard): Board {
  const scope: BoardScope =
    raw.scopeKind === "multi_project"
      ? { kind: "multi_project", projectKeys: raw.projectKeys ?? [] }
      : { kind: "project", projectKey: raw.projectKey ?? "" };
  const board: Board = { id: raw.id, name: raw.name, scope };
  if (raw.mode) board.mode = raw.mode;
  if (raw.wipLimits) board.wipLimits = raw.wipLimits;
  if (raw.columns && raw.columns.length > 0) {
    board.columns = [...raw.columns].sort((a: BoardColumn, b: BoardColumn) => a.orderIndex - b.orderIndex);
  }
  return board;
}

function mapConfig(raw: RawBoardConfig): BoardConfig {
  return { ...raw, boards: raw.boards.map(mapBoard) };
}

// Builds the issue-query string. statusIds (a column's status set) serialize as repeated
// `statusIds=` params, which ASP.NET binds to List<string>; omitting them reads the whole board.
function buildIssueQuery(p: IssueQueryParams): string {
  const parts: string[] = [];
  for (const statusId of p.statusIds ?? []) parts.push(`statusIds=${encodeURIComponent(statusId)}`);
  if (p.q) parts.push(`q=${encodeURIComponent(p.q)}`);
  if (p.jql) parts.push(`jql=${encodeURIComponent(p.jql)}`);
  if (p.assignee) parts.push(`assigneeUserId=${encodeURIComponent(p.assignee)}`);
  if (p.priority) parts.push(`priority=${encodeURIComponent(p.priority)}`);
  if (p.type) parts.push(`type=${encodeURIComponent(p.type)}`);
  if (p.cursor) parts.push(`cursor=${encodeURIComponent(p.cursor)}`);
  if (p.limit) parts.push(`limit=${p.limit}`);
  return `?${parts.join("&")}`;
}

function scopeToPayload(scope: BoardScope): Pick<CreateBoardPayload, "scopeKind" | "projectKey" | "projectKeys"> {
  if (scope.kind === "multi_project") {
    return { scopeKind: "multi_project", projectKeys: scope.projectKeys };
  }
  return { scopeKind: "project", projectKey: scope.projectKey };
}

// Translates the frontend patch (optional-field presence) into the backend's
// *Set sentinel contract. A key PRESENT in the patch — even with a null value —
// means "apply this"; an absent key means "leave alone".
function toUpdatePayload(patch: UpdateIssueInput): UpdateIssuePayload {
  const out: UpdateIssuePayload = {};
  if ("summary" in patch) out.summary = patch.summary;
  if ("descriptionMarkdown" in patch) out.descriptionMarkdown = patch.descriptionMarkdown;
  if ("type" in patch) out.type = patch.type;
  if ("priority" in patch) out.priority = patch.priority;
  if ("statusId" in patch) out.statusId = patch.statusId;
  if ("labels" in patch) out.labels = patch.labels;
  if ("customFields" in patch) out.customFields = patch.customFields;
  if ("assigneeUserId" in patch) {
    out.assigneeUserId = patch.assigneeUserId;
    out.assigneeUserIdSet = true;
  }
  if ("storyPoints" in patch) {
    out.storyPoints = patch.storyPoints;
    out.storyPointsSet = true;
  }
  if ("startDate" in patch) {
    out.startDate = patch.startDate;
    out.startDateSet = true;
  }
  if ("dueDate" in patch) {
    out.dueDate = patch.dueDate;
    out.dueDateSet = true;
  }
  if ("sprintId" in patch) {
    out.sprintId = patch.sprintId;
    out.sprintIdSet = true;
  }
  if ("parentIssueId" in patch) {
    out.parentIssueId = patch.parentIssueId;
    out.parentIssueIdSet = true;
  }
  if ("epicId" in patch) {
    out.epicId = patch.epicId;
    out.epicIdSet = true;
  }
  if ("epicName" in patch) {
    out.epicName = patch.epicName;
    out.epicNameSet = true;
  }
  if ("epicColor" in patch) {
    out.epicColor = patch.epicColor;
    out.epicColorSet = true;
  }
  if ("originalEstimateMin" in patch) {
    out.originalEstimateMin = patch.originalEstimateMin;
    out.originalEstimateMinSet = true;
  }
  if ("remainingEstimateMin" in patch) {
    out.remainingEstimateMin = patch.remainingEstimateMin;
    out.remainingEstimateMinSet = true;
  }
  if ("timeSpentMin" in patch) {
    out.timeSpentMin = patch.timeSpentMin;
    out.timeSpentMinSet = true;
  }
  if ("fixVersionId" in patch) {
    out.fixVersionId = patch.fixVersionId;
    out.fixVersionIdSet = true;
  }
  return out;
}

// All transport rejects on failure: exceptionHandler shows the user-facing toast,
// then the error is rethrown so the caller never treats a failure as success and
// the local cache is left untouched.
async function get<T>(path: string): Promise<T> {
  try {
    const response: AxiosResponse<T> = await Axios.get<T, AxiosResponse<T>>(`${boardUrl()}${path}`);
    return response.data;
  } catch (error: unknown) {
    KMainFunctions.exceptionHandler(error);
    throw error;
  }
}

// Mutations carry this tab's id so the server-side live-sync publisher can exclude the origin
// tab (it already applied the change locally) when fanning the event out to other tabs. Each one
// runs under `trackBoardWrite`, so a reload while it is in flight asks before aborting it.
const mutationConfig: AxiosRequestConfig = { headers: { "X-Board-Client-Id": BOARD_CLIENT_ID } };

// A POST that only reads (a validity check) or keeps bookkeeping (read receipts) loses the user
// nothing when a reload aborts it, so it must not make the page ask before leaving.
async function postWithoutUnloadGuard<T>(path: string, body?: unknown): Promise<T> {
  try {
    const response: AxiosResponse<T> = await Axios.post<T, AxiosResponse<T>>(
      `${boardUrl()}${path}`,
      body ?? {},
      mutationConfig
    );
    return response.data;
  } catch (error: unknown) {
    KMainFunctions.exceptionHandler(error);
    throw error;
  }
}

async function post<T>(path: string, body?: unknown): Promise<T> {
  try {
    const response: AxiosResponse<T> = await trackBoardWrite((): Promise<AxiosResponse<T>> =>
      Axios.post<T, AxiosResponse<T>>(`${boardUrl()}${path}`, body ?? {}, mutationConfig)
    );
    return response.data;
  } catch (error: unknown) {
    KMainFunctions.exceptionHandler(error);
    throw error;
  }
}

async function put<T>(path: string, body?: unknown): Promise<T> {
  try {
    const response: AxiosResponse<T> = await trackBoardWrite((): Promise<AxiosResponse<T>> =>
      Axios.put<T, AxiosResponse<T>>(`${boardUrl()}${path}`, body ?? {}, mutationConfig)
    );
    return response.data;
  } catch (error: unknown) {
    KMainFunctions.exceptionHandler(error);
    throw error;
  }
}

async function del<T>(path: string): Promise<T> {
  try {
    const response: AxiosResponse<T> = await trackBoardWrite((): Promise<AxiosResponse<T>> =>
      Axios.delete<T, AxiosResponse<T>>(`${boardUrl()}${path}`, mutationConfig)
    );
    return response.data;
  } catch (error: unknown) {
    KMainFunctions.exceptionHandler(error);
    throw error;
  }
}

// ── Client ───────────────────────────────────────────────────────────────

export class BoardApi {
  // ── On-demand loading ────────────────────────────────────────────────────

  // Bounded lookups for the board shell.
  static async getConfig(): Promise<BoardConfig> {
    const raw: RawBoardConfig = await get<RawBoardConfig>("/config");
    return mapConfig(raw);
  }

  // A cursor-paginated page of light issue cards for one column.
  static queryIssues(boardId: string, params: IssueQueryParams): Promise<IssuePage> {
    return get<IssuePage>(`/${boardId}/issues${buildIssueQuery(params)}`);
  }

  // Project-wide issue navigator (every issue in the project, across boards/statuses).
  static queryProjectIssues(projectKey: string, params: IssueQueryParams): Promise<IssuePage> {
    return get<IssuePage>(`/projects/${projectKey}/issues${buildIssueQuery(params)}`);
  }

  // Local-first sync: the board's whole active working set in one request. The client caches
  // it and runs column-grouping/filters/JQL/sort in memory. `cap` bounds the payload (0 = server
  // default); a truncated result tells the caller to fall back to per-column queries.
  static syncBoard(boardId: string, cap: number = 0): Promise<BoardSyncResult> {
    const query: string = cap > 0 ? `?cap=${cap}` : "";
    return get<BoardSyncResult>(`/${boardId}/sync${query}`);
  }

  // Full issue detail, fetched when a drawer/page opens.
  static getIssueDetail(issueId: string): Promise<IssueDetailBundle> {
    return get<IssueDetailBundle>(`/issues/${issueId}/detail`);
  }

  // Resolve a single full issue by its human key (for the full-page route).
  static getIssueByKey(key: string): Promise<Issue> {
    return get<Issue>(`/issues/by-key/${encodeURIComponent(key)}`);
  }

  // Archived issues for a board (archived-issues modal), newest-archived first.
  static getArchivedIssues(boardId: string): Promise<IssueCard[]> {
    return get<IssueCard[]>(`/${boardId}/archived`);
  }

  // A short-lived presigned URL for the attachment, fetched only when opened/downloaded.
  // inline=true serves previewable types (image/video/pdf) in the browser; the default
  // downloads them under the original filename.
  static async getAttachmentContent(attachmentId: string, inline: boolean = false): Promise<string | null> {
    const query: string = inline ? "?inline=true" : "";
    const result: AttachmentContentResult = await get<AttachmentContentResult>(`/attachments/${attachmentId}${query}`);
    return result?.dataUrl ?? null;
  }

  // Server-side command-palette search → matched issue cards.
  static searchIssues(q: string, limit: number = 25): Promise<IssueCard[]> {
    return get<IssueCard[]>(`/search?q=${encodeURIComponent(q)}&limit=${limit}`);
  }

  // Board onboarding: ask the workspace's managers (owners and supervisors) to set up / grant access.
  // The request is persisted server-side and they're emailed (unless one was sent recently). Returns
  // how many were notified — 0 means none found (tell the user to contact an admin) or throttled.
  static async requestAccess(): Promise<number> {
    const result: RequestAccessResult = await post<RequestAccessResult>("/request-access");
    return result?.notified ?? 0;
  }

  // Onboarding: the current user's board access state — whether they may initialize an empty
  // board (owner) and whether they've already requested access (durable across refreshes).
  static async getAccess(): Promise<BoardAccessResult> {
    const result: BoardAccessResult = await get<BoardAccessResult>("/access");
    return { canInitialize: result?.canInitialize ?? false, accessRequested: result?.accessRequested ?? false };
  }

  // Owner-only: initialize an empty board (default columns + a project per platform
  // project). The caller refreshes the snapshot afterwards.
  static async initialize(): Promise<void> {
    await post<void>("/initialize");
  }

  // Notifications — the backend returns only the current user's (recipient
  // resolved server-side), so there is no client-side recipient filtering.
  static getNotifications(): Promise<BoardNotification[]> {
    return get<BoardNotification[]>("/notifications");
  }

  static markNotificationsRead(): Promise<void> {
    return postWithoutUnloadGuard<void>("/notifications/read");
  }

  // Saved views — backend returns only the current user's.
  static getViews(): Promise<BoardView[]> {
    return get<BoardView[]>("/views");
  }

  static createView(name: string, href: string): Promise<BoardView> {
    return post<BoardView>("/views", { name, href });
  }

  static removeView(viewId: string): Promise<void> {
    return del<void>(`/views/${viewId}`);
  }

  // Server-computed analytics (board-wide, scoped to the board's projects).
  static getReport(boardId: string): Promise<BoardReport> {
    return get<BoardReport>(`/reports/${boardId}`);
  }

  static getEpics(boardId: string): Promise<EpicCard[]> {
    return get<EpicCard[]>(`/${boardId}/epics`);
  }

  // Bulk import of already-normalized issues (owner-only). See board-import-wizard.
  static importIssues(projectKey: string, issues: ImportIssuePayload[]): Promise<ImportResult> {
    return post<ImportResult>("/import", { projectKey, issues });
  }

  static getSprintBurndown(sprintId: string): Promise<SprintBurndown> {
    return get<SprintBurndown>(`/sprints/${sprintId}/burndown`);
  }

  static getRoadmapLinks(boardId: string): Promise<IssueLink[]> {
    return get<IssueLink[]>(`/roadmap/${boardId}/links`);
  }

  static getRoadmap(boardId: string): Promise<RoadmapRow[]> {
    return get<RoadmapRow[]>(`/roadmap/${boardId}`);
  }

  // Issues
  static createIssue(input: CreateIssueInput): Promise<Issue> {
    return post<Issue>("/issues", input);
  }

  static cloneIssue(issueId: string): Promise<Issue> {
    return post<Issue>(`/issues/${issueId}/clone`);
  }

  static updateIssue(issueId: string, patch: UpdateIssueInput): Promise<Issue> {
    return put<Issue>(`/issues/${issueId}`, toUpdatePayload(patch));
  }

  static deleteIssue(issueId: string): Promise<void> {
    return del<void>(`/issues/${issueId}`);
  }

  static moveIssue(input: MoveIssueInput): Promise<Issue> {
    return post<Issue>("/issues/move", input);
  }

  static archiveIssue(issueId: string): Promise<void> {
    return post<void>(`/issues/${issueId}/archive`);
  }

  static restoreIssue(issueId: string): Promise<void> {
    return post<void>(`/issues/${issueId}/restore`);
  }

  static toggleWatch(issueId: string): Promise<void> {
    return post<void>(`/issues/${issueId}/watch`);
  }

  static createSubtask(parentIssueId: string, summary: string): Promise<Issue> {
    return post<Issue>(`/issues/${parentIssueId}/subtasks`, { summary });
  }

  static promoteSubtask(subtaskId: string): Promise<boolean> {
    return post<boolean>(`/issues/${subtaskId}/promote`);
  }

  static toggleSubtaskComplete(subtaskId: string): Promise<void> {
    return post<void>(`/issues/${subtaskId}/toggle-complete`);
  }

  // Comments
  static addComment(issueId: string, bodyMarkdown: string, id?: string): Promise<IssueComment> {
    return post<IssueComment>(`/issues/${issueId}/comments`, { bodyMarkdown, id });
  }

  static editComment(commentId: string, bodyMarkdown: string): Promise<boolean> {
    return put<boolean>(`/comments/${commentId}`, { bodyMarkdown });
  }

  static deleteComment(commentId: string): Promise<void> {
    return del<void>(`/comments/${commentId}`);
  }

  static toggleCommentReaction(commentId: string, emoji: string): Promise<void> {
    return post<void>(`/comments/${commentId}/reactions/toggle`, { emoji });
  }

  // Best-effort link unfurl — returns null on 204 (nothing to show) or any error (the preview is
  // optional and must never surface an error toast).
  static async getLinkPreview(url: string): Promise<LinkPreview | null> {
    try {
      const response: AxiosResponse<LinkPreview> = await Axios.get<LinkPreview>(`${boardUrl()}/link-preview`, {
        params: { url }
      });
      return response.status === 204 || !response.data ? null : response.data;
    } catch {
      return null;
    }
  }

  // Links
  static wouldCreateBlockingCycle(fromIssueId: string, toIssueId: string, type: IssueLinkType): Promise<boolean> {
    const body: AddLinkPayload = { fromIssueId, toIssueId, type };
    return postWithoutUnloadGuard<boolean>("/links/check-cycle", body);
  }

  static addLink(fromIssueId: string, toIssueId: string, type: IssueLinkType): Promise<IssueLink | null> {
    const body: AddLinkPayload = { fromIssueId, toIssueId, type };
    return post<IssueLink | null>("/links", body);
  }

  static removeLink(linkId: string): Promise<void> {
    return del<void>(`/links/${linkId}`);
  }

  // Boards
  static async createBoard(name: string, scope: BoardScope, mode: BoardMode): Promise<Board> {
    const payload: CreateBoardPayload = { name, mode, ...scopeToPayload(scope) };
    const raw: RawBoard = await post<RawBoard>("/boards", payload);
    return mapBoard(raw);
  }

  // Creates (or returns the existing) board project tied to a platform project. `code` is the
  // optional project key; blank lets the server derive one from the name.
  static createProject(platformProjectId: string, name: string, code?: string): Promise<Project> {
    return post<Project>("/projects", { platformProjectId, name, code });
  }

  static renameProject(key: string, name: string): Promise<void> {
    return put<void>(`/projects/${key}`, { name });
  }

  static deleteProject(key: string): Promise<void> {
    return del<void>(`/projects/${key}`);
  }

  static setProjectTransitions(key: string, transitions: BoardTransition[]): Promise<void> {
    return put<void>(`/projects/${key}/transitions`, { transitions });
  }

  static renameBoard(boardId: string, name: string): Promise<void> {
    return put<void>(`/boards/${boardId}/name`, { name });
  }

  static setBoardMode(boardId: string, mode: BoardMode): Promise<void> {
    return put<void>(`/boards/${boardId}/mode`, { mode });
  }

  static setBoardColumns(boardId: string, columns: BoardColumn[]): Promise<void> {
    return put<void>(`/boards/${boardId}/columns`, { columns });
  }

  static deleteBoard(boardId: string): Promise<void> {
    return del<void>(`/boards/${boardId}`);
  }

  static setWipLimit(boardId: string, columnId: string, limit: number | null): Promise<void> {
    return put<void>(`/boards/${boardId}/wip-limit`, { columnId, limit });
  }

  // Sprints
  static createSprint(projectKey: string, name: string, goal: string): Promise<Sprint> {
    return post<Sprint>("/sprints", { projectKey, name, goal });
  }

  static startSprint(sprintId: string, endDate: string | null): Promise<void> {
    return post<void>(`/sprints/${sprintId}/start`, { endDate });
  }

  static completeSprint(sprintId: string): Promise<void> {
    return post<void>(`/sprints/${sprintId}/complete`);
  }

  static deleteSprint(sprintId: string): Promise<void> {
    return del<void>(`/sprints/${sprintId}`);
  }

  static assignIssueToSprint(issueId: string, sprintId: string | null): Promise<void> {
    return post<void>("/sprints/assign", { issueId, sprintId });
  }

  // Statuses
  static addStatus(projectKey: string, name: string, category: BoardStatus["category"]): Promise<BoardStatus> {
    return post<BoardStatus>("/statuses", { projectKey, name, category });
  }

  static updateStatus(
    statusId: string,
    name: string | undefined,
    category: BoardStatus["category"] | undefined
  ): Promise<void> {
    return put<void>(`/statuses/${statusId}`, { name, category });
  }

  static deleteStatus(statusId: string): Promise<void> {
    return del<void>(`/statuses/${statusId}`);
  }

  static reorderStatuses(projectKey: string, orderedIds: string[]): Promise<void> {
    return post<void>("/statuses/reorder", { projectKey, orderedIds });
  }

  // Labels
  static ensureLabel(name: string): Promise<void> {
    return post<void>("/labels", { name });
  }

  static setLabelColor(name: string, color: string): Promise<void> {
    return put<void>("/labels/color", { name, color });
  }

  // Versions
  static createVersion(projectKey: string, name: string): Promise<Version> {
    return post<Version>("/versions", { projectKey, name });
  }

  static updateVersion(
    id: string,
    name: string,
    releaseDate: string | null,
    released: boolean,
    description: string | null
  ): Promise<void> {
    return put<void>(`/versions/${id}`, { name, releaseDate, released, description });
  }

  static deleteVersion(id: string): Promise<void> {
    return del<void>(`/versions/${id}`);
  }

  // Per-release issue progress (total + done) for the project's Releases page.
  static getVersionProgress(projectKey: string): Promise<VersionProgress[]> {
    return get<VersionProgress[]>(`/projects/${encodeURIComponent(projectKey)}/version-progress`);
  }

  // CSV export of the project's issues (counterpart to import). Returns the CSV text + filename.
  static exportIssues(projectKey: string): Promise<IssuesExportResult> {
    return get<IssuesExportResult>(`/projects/${encodeURIComponent(projectKey)}/export`);
  }

  // Custom fields
  static createCustomField(
    projectKey: string,
    name: string,
    type: CustomFieldType,
    options: string[],
    issueTypes: IssueType[]
  ): Promise<CustomFieldDef> {
    return post<CustomFieldDef>("/custom-fields", { projectKey, name, type, options, issueTypes });
  }

  static updateCustomField(
    fieldId: string,
    name: string,
    options: string[],
    issueTypes: IssueType[]
  ): Promise<CustomFieldDef> {
    return put<CustomFieldDef>(`/custom-fields/${fieldId}`, { name, options, issueTypes });
  }

  static deleteCustomField(fieldId: string): Promise<void> {
    return del<void>(`/custom-fields/${fieldId}`);
  }

  static reorderCustomFields(projectKey: string, orderedIds: string[]): Promise<void> {
    return post<void>("/custom-fields/reorder", { projectKey, orderedIds });
  }

  static setCustomFieldValue(issueId: string, fieldId: string, value: string): Promise<void> {
    return put<void>(`/issues/${issueId}/custom-fields/${fieldId}`, { value });
  }

  // Attachments
  static addAttachment(
    issueId: string,
    fileName: string,
    mimeType: string,
    dataUrl: string,
    sizeBytes: number
  ): Promise<Attachment | null> {
    return post<Attachment | null>("/attachments", { issueId, fileName, mimeType, dataUrl, sizeBytes });
  }

  static removeAttachment(attachmentId: string): Promise<void> {
    return del<void>(`/attachments/${attachmentId}`);
  }

  // Artifact links
  static addArtifactLink(
    issueId: string,
    kind: ArtifactKind,
    refId: string,
    displayName: string
  ): Promise<ArtifactLink | null> {
    return post<ArtifactLink | null>("/artifact-links", { issueId, kind, refId, displayName });
  }

  static removeArtifactLink(linkId: string): Promise<void> {
    return del<void>(`/artifact-links/${linkId}`);
  }

  static createIssueFromArtifact(
    projectName: string,
    kind: ArtifactKind,
    refId: string,
    displayName: string,
    summary: string
  ): Promise<Issue | null> {
    return post<Issue | null>("/artifact-links/create-issue", { projectName, kind, refId, displayName, summary });
  }

  // Automations
  static createAutomation(
    projectKey: string,
    name: string,
    trigger: AutomationTrigger,
    action: AutomationAction,
    notifyOnRun: boolean
  ): Promise<AutomationRule> {
    return post<AutomationRule>("/automations", { projectKey, name, trigger, action, notifyOnRun });
  }

  static toggleAutomation(ruleId: string): Promise<void> {
    return post<void>(`/automations/${ruleId}/toggle`);
  }

  static deleteAutomation(ruleId: string): Promise<void> {
    return del<void>(`/automations/${ruleId}`);
  }
}
